import React, { useEffect, useMemo, useRef, useState } from 'react';
import useSingleQuery from '../../hook/useSingleQuery';
import { fetchGetSessionReview } from '../../services/useSessionReviewQueryService';
import { Col, getItemWithExpiration, getSatisfactionReturnEmojiText, Row } from '../../lib/utils';
import dayjs from 'dayjs';
import { createColumnHelper } from '@tanstack/react-table';
import { LatestSessionReview, SessionReview } from '../../services/useFeedDashboardQueryService';
import { BoldText, DemiLightText, MediumText, RegularText } from '../../lib/Text';
import Table from '../../components/Table/Table';
import { fetchCoachNameList } from '../../services/useCenterQueryService';
import Dropdown from '../../components/DropDown/DropDown';
import { colors } from '../../lib/colors';
import { CodeValueType } from '../../types/types';
import { PAGINATIONOPTIONS } from '../../lib/varialbe';
import SvgIcon from '../../components/SvgIcon';
import { TooltipWrapper } from '../../components/TooltipWrapper';
import ModalFeedCoachStatisticsPageAllMember from './ModalFeedCoachStatisticsPageAllMember';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import { DateRange, DateRangePicker } from 'react-date-range';
import { ko } from 'date-fns/locale';
import { recoilCenterReportMonth } from '@/atoms/atom';
import { useRecoilState } from 'recoil';
import { isEmpty } from 'lodash';
const columnHelper = createColumnHelper<SessionReview>();

type ReviewType = 'all' | 'normal' | 'anonymous';
type SatisfactionType = 'all' | 'satisfied' | 'neutral' | 'bad';

const reviewTypeOptions = [
    { code: 'normal', value: '일반 리뷰' },
    { code: 'anonymous', value: '익명 리뷰' },
];

const satisfactionOptions = [
    { code: 'satisfied', value: '만족' },
    { code: 'neutral', value: '보통' },
    { code: 'bad', value: '불만족' },
];

// 상태, 강사, 회원, 만족도, 피드백, 리뷰, 수업일시, 평가일시
const columns = [
    columnHelper.accessor('score', {
        id: 'score',
        header: ({ header }) => (
            <Row className="flex items-center cursor-pointer" onClick={() => header.column.toggleSorting()}>
                <RegularText fontSize={16} lineHeight={20} color={'#5D6166'} className="mr-[2px]">
                    만족도
                </RegularText>
                <TooltipWrapper
                    tooltip={{
                        opacity: 1,
                        customContent: (
                            <Col className="flex items-start justify-center px-[20px] py-[10px] rounded-[9px] gap-[8px]">
                                <Row className="flex items-center">
                                    <SvgIcon
                                        name={'SvgSatisfactionSatisfied'}
                                        size={26}
                                        fill={colors.white}
                                        className="mr-[12px]"
                                    />
                                    <DemiLightText fontSize={13} lineHeight={18} color={colors.white}>
                                        만족 (80% 이상)
                                    </DemiLightText>
                                </Row>
                                <Row className="flex items-center">
                                    <SvgIcon
                                        name={'SvgSatisfactionNeutral'}
                                        size={26}
                                        fill={colors.white}
                                        className="mr-[12px]"
                                    />
                                    <DemiLightText fontSize={13} lineHeight={18} color={colors.white}>
                                        보통 (50 ~ 80%)
                                    </DemiLightText>
                                </Row>
                                <Row className="flex items-center">
                                    <SvgIcon
                                        name={'SvgSatisfactionBad'}
                                        size={26}
                                        fill={colors.white}
                                        className="mr-[12px]"
                                    />
                                    <DemiLightText fontSize={13} lineHeight={18} color={colors.white}>
                                        불만족 (0 ~ 40%)
                                    </DemiLightText>
                                </Row>
                            </Col>
                        ),
                    }}
                >
                    <SvgIcon name="SvgInfoInCircle" size={14} fill={'#B4BCC4'} className="mr-[6px]" />
                </TooltipWrapper>
                {header.column.getIsSorted() === 'asc' && <SvgIcon name="SvgSortArrow" fill={'#427BE8'} size={14} />}
                {header.column.getIsSorted() === 'desc' && (
                    <SvgIcon name="SvgSortArrow" fill={'#427BE8'} size={14} className="rotate-180 " />
                )}
                {!header.column.getIsSorted() && (
                    <Col className="gap-[1px] ml-[4.3px]">
                        <SvgIcon name="SvgFillArrow" size={9} fill="#C6C8C9" />
                        <SvgIcon name="SvgFillArrow" size={9} fill="#C6C8C9" className="rotate-180" />
                    </Col>
                )}
            </Row>
        ),
        cell: (info) => (
            <Row className="flex items-center ">
                <SvgIcon
                    name={getSatisfactionReturnEmojiText(info.getValue())?.svgIconName as any}
                    size={26}
                    fill={colors.white}
                    className="mr-[12px]"
                />
                <RegularText fontSize={16} lineHeight={21} color={'#3D4244'}>
                    {info.getValue() ? info.getValue() + '%' : '-'}
                </RegularText>
            </Row>
        ),
        size: 118,
        enableSorting: true,
        meta: {
            isSortIconVisible: false,
        },
    }),
    columnHelper.accessor('coachName', {
        id: 'coachName',
        header: () => (
            <RegularText fontSize={16} lineHeight={21} color={'#5D6166'}>
                강사
            </RegularText>
        ),
        cell: (info) => (
            <RegularText fontSize={16} lineHeight={21} color={'#3D4244'}>
                {info.getValue()}
            </RegularText>
        ),
        size: 107,
        enableSorting: false,
    }),
    columnHelper.accessor('memberName', {
        id: 'memberName',
        header: () => (
            <RegularText fontSize={16} lineHeight={21} color={'#5D6166'}>
                회원
            </RegularText>
        ),
        cell: (info) => {
            const isAnonymous = info.row.original.isAnonymous;
            // const isAnonymous = true; // 임시로 false로 설정, 실제 데이터에 따라 변경 필요

            return (
                <Row className="flex items-center justify-between w-full px-[6px]">
                    <RegularText fontSize={18} lineHeight={23} color={'#5D6166'} letterSpacing={-0.5}>
                        {info.getValue()}
                    </RegularText>
                    {isAnonymous && (
                        <TooltipWrapper
                            tooltip={{
                                customContent: (
                                    <Col className="flex items-start justify-center px-[20px] py-[10px]  rounded-[9px] gap-[10px]">
                                        <DemiLightText fontSize={13} lineHeight={18} color={colors.white}>
                                            * 관리자만 볼 수 있는 리뷰를 남긴 회원이에요.
                                        </DemiLightText>
                                        <DemiLightText fontSize={13} lineHeight={18} color={colors.white}>
                                            * 강사님은 해당 리뷰를 확인할 수 없어요.
                                        </DemiLightText>
                                        <BoldText fontSize={13} lineHeight={18} color={colors.white}>
                                            * 회원의 솔직한 의견이 반영될 수 있도록 정보 보호에 유의해 주세요
                                        </BoldText>
                                    </Col>
                                ),
                                opacity: 1,
                            }}
                        >
                            <Row className="flex items-center justify-center bg-Text_1 px-[7px] py-[6px] rounded-[8px] ">
                                <SvgIcon name="SvgSecretReview" size={17} fill={colors.white} className="mr-[4px]" />
                                <DemiLightText fontSize={12} lineHeight={17} color={'#FDFEFF'}>
                                    리뷰
                                </DemiLightText>
                            </Row>
                        </TooltipWrapper>
                    )}
                </Row>
            );
        },
        size: 157,
        enableSorting: false,
    }),

    columnHelper.accessor('content', {
        id: 'content',
        header: () => (
            <RegularText fontSize={16} lineHeight={20} color={'#5D6166'}>
                리뷰
            </RegularText>
        ),
        cell: (info) => (
            <RegularText
                fontSize={16}
                lineHeight={21}
                color={colors.Text_1}
                letterSpacing={-0.5}
                className={`flex w-full whitespace-nowrap overflow-hidden text-ellipsis max-w-[217px] px-[9px] 
                            ${isEmpty(info?.getValue()) ? 'text-center justify-center' : ''}
                            `}
            >
                {isEmpty(info?.getValue()) ? '-' : info.getValue()}
            </RegularText>
        ),
        size: 217,
        enableSorting: false,
    }),
    columnHelper.accessor('session', {
        id: 'session',
        header: (info) => (
            <Row
                className="flex items-center cursor-pointer"
                onClick={() => {
                    info.column.toggleSorting();
                }}
            >
                <RegularText fontSize={16} lineHeight={20} color={'#5D6166'}>
                    회차
                </RegularText>
            </Row>
        ),
        cell: (info) => (
            <RegularText fontSize={16} lineHeight={21} color={'#5D6166'}>
                {/* 10/10회 */}
                {info.getValue() ? `${info.getValue()}/${info.row.original.totalSession}회` : '-'}
            </RegularText>
        ),
        size: 116,
        enableSorting: true,
    }),
    columnHelper.accessor('scheduleStartTime', {
        id: 'scheduleStartTime',
        header: (info) => (
            <Row
                className="flex items-center cursor-pointer"
                onClick={() => {
                    info.column.toggleSorting();
                }}
            >
                <RegularText fontSize={16} lineHeight={20} color={'#5D6166'}>
                    수업 일시
                </RegularText>
            </Row>
        ),
        cell: (info) => (
            <RegularText fontSize={18} lineHeight={23} color={'#5D6166'}>
                {dayjs(info.getValue()).format('YYYY-MM-DD(ddd) HH:mm')}
            </RegularText>
        ),
        size: 213,
        enableSorting: true,
    }),
    columnHelper.accessor('reviewCreatedAt', {
        id: 'reviewCreatedAt',
        header: (info) => (
            <Row
                className="flex items-center cursor-pointer"
                onClick={() => {
                    info.column.toggleSorting();
                }}
            >
                <RegularText fontSize={16} lineHeight={20} color={'#5D6166'}>
                    평가 일시
                </RegularText>
            </Row>
        ),
        cell: (info) => (
            <RegularText fontSize={18} lineHeight={23} color={'#5D6166'}>
                {dayjs(info.getValue()).format('YYYY-MM-DD(ddd) HH:mm')}
            </RegularText>
        ),
        size: 213,
        enableSorting: true,
    }),
];
// 일지 작성률

const FeedMemberSatisfactionPage = () => {
    const tableRef = useRef<any>(null);
    const authObject = getItemWithExpiration('authObject');
    const [month, setMonth] = useRecoilState(recoilCenterReportMonth);
    const [startDate, setStartDate] = useState(dayjs(month).startOf('month').toISOString());
    // 이번달일경우 오늘이 endDate
    const [endDate, setEndDate] = useState(
        dayjs(month).isSame(dayjs(), 'month') ? dayjs().toISOString() : dayjs(month).endOf('month').toISOString()
    );
    const [tmpStartDate, setTmpStartDate] = useState(dayjs(month).startOf('month').toISOString());
    const [tmpEndDate, setTmpEndDate] = useState(
        dayjs(month).isSame(dayjs(), 'month') ? dayjs().toISOString() : dayjs(month).endOf('month').toISOString()
    );
    const [apiStartDate, setApiStartDate] = useState(() => dayjs(month).startOf('month').toISOString());
    const [apiEndDate, setApiEndDate] = useState(() =>
        dayjs(month).isSame(dayjs(), 'month') ? dayjs().toISOString() : dayjs(month).endOf('month').toISOString()
    );
    // ✅ startDate, endDate가 바뀔 때마다 업데이트
    useEffect(() => {
        setApiStartDate(startDate);
        setApiEndDate(endDate);
    }, [startDate, endDate]);
    const calendarButtonRef = useRef<HTMLDivElement>(null);
    const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 });

    const startDateFormatted = dayjs(startDate).format('YYYY-MM-DD');
    const endDateFormatted = dayjs(endDate).format('YYYY-MM-DD');

    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

    const handleDateRangeChange = (ranges: any) => {
        const range = ranges.selection;
        setTmpStartDate(dayjs(range.startDate).toISOString());
        setTmpEndDate(dayjs(range.endDate).toISOString());
        // setStartDate, setEndDate 제거
    };
    const handleToggleCalendar = () => {
        setIsDatePickerOpen((prev) => !prev);

        if (calendarButtonRef.current) {
            const rect = calendarButtonRef.current.getBoundingClientRect();
            setCalendarPosition({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
            });
        }
    };
    const dateRange = {
        startDate: new Date(tmpStartDate),
        endDate: new Date(tmpEndDate),
        key: 'selection',
        format: 'yyyy-MM-dd',
    };

    const [selectedCoach, setSelectedCoach] = useState('all');
    // 리뷰 : 전체, 일반, 익명
    const [selectedReviewType, setSelectedReviewType] = useState<ReviewType>('all');
    // 만족도 : 만족, 보통, 불만족
    const [selectedSatisfaction, setSelectedSatisfaction] = useState<SatisfactionType>('all');
    // 페이지네이션 : 10, 20, 30
    const [selectedPagination, setSelectedPagination] = useState('10');

    const [selectedSessionReview, setSelectedSessionReview] = useState<LatestSessionReview | undefined>(undefined);

    const [isSessionReviewModalOpen, setIsSessionReviewModalOpen] = useState(false);

    const { refinedResult, isLoading, isFetching } = useSingleQuery({
        fetchFn: fetchGetSessionReview,
        params: { centerId: authObject.centerId, startDate: apiStartDate, endDate: apiEndDate },
        enabled: !!authObject,
        suspense: false,
    });

    const { refinedResult: _result } = useSingleQuery({
        fetchFn: fetchCoachNameList,
        params: { centerId: authObject?.centerId, isAllConcat: true },
        enabled: !!authObject?.centerId,
        suspense: false,
    });

    const sessionReview = ((refinedResult as any)?.data?.data?.sessionReviews as SessionReview[]) || [];

    const coachList: CodeValueType<string>[] = _result?.data as CodeValueType<string>[];

    const filteredSessionReview = useMemo(() => {
        return sessionReview.filter((review: any) => {
            const coachMatch = selectedCoach === 'all' || review.coachId === selectedCoach;

            const reviewTypeMatch =
                selectedReviewType === 'all'
                    ? true
                    : selectedReviewType === 'normal'
                    ? !review.isAnonymous
                    : review.isAnonymous;

            const satisfactionMatch =
                selectedSatisfaction === 'all'
                    ? true
                    : selectedSatisfaction === 'satisfied'
                    ? review.score >= 80
                    : selectedSatisfaction === 'neutral'
                    ? review.score >= 50 && review.score < 80
                    : review.score < 50;

            return coachMatch && reviewTypeMatch && satisfactionMatch;
        });
    }, [sessionReview, selectedCoach, selectedReviewType, selectedSatisfaction]);

    const isSelectedFn = (startDate: any, endDate: any) => {
        let selected = false;
        if (
            startDate.format('YYYYMMDD') === dayjs(startDate).format('YYYYMMDD') &&
            endDate.format('YYYYMMDD') === dayjs(endDate).format('YYYYMMDD')
        ) {
            selected = true;
        }
        return selected;
    };

    return (
        <Col className="flex w-full h-full bg-white py-[34px] px-[46px] rounded-[32px]">
            <Row className="flex items-center mb-[57px] justify-between">
                <Row
                    ref={calendarButtonRef}
                    className="items-center w-[400px] bg-white border border-solid border-[#CCCBD0] rounded-[8px] px-[20px] py-[10px] justify-between cursor-pointer"
                    onClick={handleToggleCalendar}
                >
                    <Row className="flex items-center">
                        <SvgIcon
                            name="SvgCalendar"
                            size={18}
                            fill={colors.white}
                            stroke={colors.primary}
                            className="mr-[14px]"
                        />
                        {/* 이번 달 (2025. 4. 1 - 2025. 4. 28) */}
                        <RegularText fontSize={18} lineHeight={23} color={colors.Text_1}>
                            {dayjs(startDate).format('YYYY. M. D')} - {dayjs(endDate).format('YYYY. M. D')}
                        </RegularText>
                    </Row>
                    <SvgIcon name="SvgDownStrokeArrow" size={17} fill={colors.white} stroke={colors.primary} />
                </Row>
                <Row className="flex items-center gap-[16px]">
                    {coachList && coachList.length > 0 && (
                        <Dropdown
                            initialSelected={selectedCoach}
                            options={coachList}
                            onSelect={(selected) => {
                                // handleSelect(selected, 'coach');
                                setSelectedCoach(selected.code);
                            }}
                            bgColor="#F2F4F6"
                            textColor={colors.Text_1}
                            borderRadius={7}
                            totalText="모든 강사"
                            arrowColor={colors.Text_1}
                            className="py-[5px] px-[10px]"
                            textType="regular"
                        />
                    )}
                    <Dropdown
                        initialSelected={selectedReviewType}
                        options={reviewTypeOptions}
                        onSelect={(selected) => {
                            setSelectedReviewType(selected.code as ReviewType);
                        }}
                        bgColor="#F2F4F6"
                        textColor={colors.Text_1}
                        borderRadius={7}
                        totalText="모든 리뷰"
                        arrowColor={colors.Text_1}
                        className="py-[5px] px-[10px]"
                        textType="regular"
                    />
                    <Dropdown
                        initialSelected={selectedSatisfaction}
                        options={satisfactionOptions}
                        onSelect={(selected) => {
                            setSelectedSatisfaction(selected.code as SatisfactionType);
                        }}
                        bgColor="#F2F4F6"
                        textColor={colors.Text_1}
                        borderRadius={7}
                        totalText="모든 만족도"
                        arrowColor={colors.Text_1}
                        className="py-[5px] px-[10px]"
                        textType="regular"
                    />
                    <Dropdown
                        initialSelected={selectedPagination}
                        options={PAGINATIONOPTIONS}
                        onSelect={(selected) => {
                            setSelectedPagination(selected.code);
                            tableRef?.current?.table?.setPageSize(Number(selected.code));
                        }}
                        bgColor="#F2F4F6"
                        textColor={colors.Text_1}
                        borderRadius={7}
                        isAllOption={false}
                        arrowColor={colors.Text_1}
                        className="py-[5px] px-[10px]"
                        textType="regular"
                    />

                    {/* <MemberSearchTextInput
                        data={memberData}
                        width={154}
                        placeholder="회원 검색"
                        onSelect={(select) => setSearch(select.value)}
                        onSearch={(search) => {
                            setSearch(search);
                        }}
                    /> */}
                </Row>
            </Row>
            {sessionReview && (
                <Table
                    ref={tableRef}
                    columns={columns}
                    data={filteredSessionReview}
                    headerColor="white"
                    isUnballancedRowColor
                    indexColumnWidth={30}
                    rowHeight={84}
                    className="mb-[30px]"
                    isPagination
                    customPaginationComponent={({
                        firstPage,
                        nextPage,
                        canNextPage,
                        previousPage,
                        canPreviousPage,
                        pageIndex,
                        totalPageIndex,
                        setPageIndex,
                    }) => {
                        const totalPages = totalPageIndex || 0;
                        const currentPage = (pageIndex || 0) + 1;

                        return (
                            <Row className="flex items-center gap-[12px] self-center my-[20px]">
                                {/* 이전 버튼 */}
                                <button
                                    onClick={previousPage}
                                    disabled={canPreviousPage}
                                    className="disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                    <SvgIcon
                                        name={'SvgLeftStrokeArrow'}
                                        size={15}
                                        fill={colors.trans}
                                        stroke={'#70747C'}
                                        strokeWidth={1.2}
                                    />
                                </button>

                                {/* 페이지 번호 */}
                                <Row className="flex items-center gap-[8px]">
                                    {Array.from({ length: totalPages }, (_, i) => (
                                        <button
                                            key={i}
                                            onClick={() => setPageIndex?.(i)}
                                            className={`w-[32px] h-[32px] rounded-full flex items-center justify-center 
                                ${currentPage === i + 1 ? 'bg-[#3D4244] ' : ' hover:bg-[#F0F0F0]'}`}
                                        >
                                            <MediumText
                                                fontSize={16}
                                                lineHeight={21}
                                                color={currentPage === i + 1 ? '#FFFFFF' : colors.Text_1}
                                            >
                                                {i + 1}
                                            </MediumText>
                                        </button>
                                    ))}
                                </Row>

                                {/* 다음 버튼 */}
                                <button
                                    onClick={nextPage}
                                    disabled={canNextPage}
                                    className="disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                    <SvgIcon
                                        name={'SvgRightStrokeArrow'}
                                        size={15}
                                        fill={colors.trans}
                                        stroke={'#70747C'}
                                        strokeWidth={1.2}
                                    />
                                </button>
                            </Row>
                        );
                    }}
                    onRowClick={(row: any) => {
                        setSelectedSessionReview(row);
                        setIsSessionReviewModalOpen(true);
                    }}
                />
            )}
            {isSessionReviewModalOpen && (
                <ModalFeedCoachStatisticsPageAllMember
                    setIsVisible={setIsSessionReviewModalOpen}
                    sessionReview={selectedSessionReview}
                />
            )}
            {isDatePickerOpen && (
                <div
                    className="absolute z-50 top-[60px] left-0 shadow-lg rounded-[12px]"
                    style={{ position: 'absolute', top: calendarPosition.top, left: calendarPosition.left, zIndex: 50 }}
                >
                    <DateRangePicker
                        ranges={[dateRange]}
                        onChange={handleDateRangeChange}
                        locale={ko}
                        renderStaticRangeLabel={(range) => <div>{range.label}</div>}
                        staticRanges={[
                            {
                                label: '지난 주',
                                hasCustomRendering: true,
                                isSelected: (rage) =>
                                    isSelectedFn(
                                        dayjs().subtract(1, 'week').startOf('week'),
                                        dayjs().subtract(1, 'week').endOf('week')
                                    ),
                                range: () => ({
                                    startDate: dayjs().subtract(1, 'week').startOf('week').toDate(),
                                    endDate: dayjs().subtract(1, 'week').endOf('week').toDate(),
                                }),
                            },
                            {
                                label: `지난 달`,
                                hasCustomRendering: true,
                                isSelected: (rage) =>
                                    isSelectedFn(
                                        dayjs().subtract(1, 'month').startOf('month'),
                                        dayjs().subtract(1, 'month').endOf('month')
                                    ),
                                range: () => ({
                                    startDate: dayjs().subtract(1, 'month').startOf('month').toDate(),
                                    endDate: dayjs().subtract(1, 'month').endOf('month').toDate(),
                                }),
                            },
                            {
                                label: `이번 달`,
                                hasCustomRendering: true,
                                isSelected: (rage) => isSelectedFn(dayjs().startOf('month'), dayjs()),
                                range: () => ({
                                    startDate: dayjs().startOf('month').toDate(),
                                    endDate: dayjs().toDate(),
                                }),
                            },
                            {
                                label: '최근 7일',
                                hasCustomRendering: true,
                                isSelected: (rage) => isSelectedFn(dayjs().subtract(1, 'week'), dayjs()),
                                range: () => ({
                                    startDate: dayjs().subtract(1, 'week').toDate(),
                                    endDate: dayjs().toDate(),
                                }),
                            },
                            {
                                label: '최근 30일',
                                hasCustomRendering: true,
                                isSelected: (rage) => isSelectedFn(dayjs().subtract(1, 'month'), dayjs()),
                                range: () => ({
                                    startDate: dayjs().subtract(1, 'month').toDate(),
                                    endDate: dayjs().toDate(),
                                }),
                            },
                            {
                                label: '최근 1년',
                                hasCustomRendering: true,
                                isSelected: (rage) => isSelectedFn(dayjs().subtract(1, 'year'), dayjs()),
                                range: () => ({
                                    startDate: dayjs().subtract(1, 'year').toDate(),
                                    endDate: dayjs().toDate(),
                                }),
                            },
                        ]}
                        dateDisplayFormat="yyyy.MM.dd"
                    />
                    <div className="flex items-center justify-end bg-white">
                        <div
                            className=" items-center justify-center bg-[#F2F4F6]  py-[10px] rounded-[8px] cursor-pointer w-[100px] h-[48px] text-center mb-[12px] mr-[12px]"
                            onClick={() => {
                                setIsDatePickerOpen(false);
                            }}
                        >
                            <RegularText fontSize={16} lineHeight={25} color={colors.Text_1}>
                                닫기
                            </RegularText>
                        </div>
                        <div
                            className=" items-center justify-center bg-[#E9F1FF]  py-[10px] rounded-[8px] cursor-pointer w-[100px] h-[48px] text-center mb-[12px] mr-[12px]"
                            onClick={() => {
                                setStartDate(tmpStartDate);
                                setEndDate(tmpEndDate);
                                setMonth(tmpStartDate);
                                setIsDatePickerOpen(false);
                            }}
                        >
                            <RegularText fontSize={16} lineHeight={25} color={colors.primary}>
                                적용
                            </RegularText>
                        </div>
                    </div>
                </div>
            )}
        </Col>
    );
};

export default FeedMemberSatisfactionPage;
